/**
 * UserAvatar - Unified Avatar Component
 * 
 * A single, consistent avatar component used throughout the entire app.
 * Handles image display with initials fallback and consistent color generation.
 * 
 * Features:
 * - Displays user profile image if available
 * - Falls back to colored circle with initials (First + Last name)
 * - Consistent color generation based on name/userId hash
 * - Multiple size presets
 * - Loading state handling
 * - Image error fallback
 * - Browser caching via AvatarCacheContext
 * 
 * Usage:
 *   <UserAvatar name="Alex Edwards" avatarUrl={user.avatar_url} size="md" />
 *   <UserAvatar userId="abc123" name="John" size="lg" />
 */

import React, { useState, useMemo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useAvatarCache } from '@/contexts';

/**
 * Avatar gradient colors - consistent across the app
 * Applied based on name/userId hash for consistency
 */
const AVATAR_GRADIENTS = [
  'from-indigo-500 to-purple-500',
  'from-blue-500 to-cyan-500', 
  'from-emerald-500 to-teal-500',
  'from-orange-500 to-rose-500',
  'from-pink-500 to-purple-500',
  'from-violet-500 to-indigo-500',
  'from-cyan-500 to-blue-500',
  'from-rose-500 to-pink-500',
  'from-amber-500 to-orange-500',
  'from-teal-500 to-emerald-500'
];

/**
 * Size presets for the avatar
 */
const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
  '2xl': 'w-20 h-20 text-xl',
  '3xl': 'w-24 h-24 text-2xl'
};

/**
 * Get initials from a name
 * - "Alex Edwards" → "AE"
 * - "Alex" → "A"  
 * - "Mary Jane Watson" → "MW" (first + last)
 * - null/undefined → "?"
 * 
 * @param {string} name - User's display name
 * @returns {string} One or two character initials
 */
export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  
  const trimmed = name.trim();
  if (!trimmed) return '?';
  
  // Split by whitespace
  const parts = trimmed.split(/\s+/).filter(Boolean);
  
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    // Single name - return first character
    return parts[0].charAt(0).toUpperCase();
  }
  
  // Multiple names - return first char of first name + first char of last name
  const firstInitial = parts[0].charAt(0);
  const lastInitial = parts[parts.length - 1].charAt(0);
  return (firstInitial + lastInitial).toUpperCase();
};

/**
 * Get consistent gradient class based on name or userId
 * Uses hash to ensure same user always gets same color
 * 
 * @param {string} identifier - Name or userId to hash
 * @returns {string} Tailwind gradient class
 */
export const getAvatarGradient = (identifier) => {
  if (!identifier) return AVATAR_GRADIENTS[0];
  
  const str = String(identifier);
  const hash = str.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

/**
 * UserAvatar Component
 * 
 * @param {Object} props
 * @param {string} props.name - User's display name (used for initials & color)
 * @param {string} props.avatarUrl - URL to the user's avatar image
 * @param {string} props.userId - Optional userId (used for caching and color)
 * @param {string} props.size - Size preset: 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showBorder - Whether to show a white border
 * @param {boolean} props.showShadow - Whether to show a shadow
 * @param {Function} props.onClick - Click handler
 */
export function UserAvatar({
  name,
  avatarUrl,
  userId,
  size = 'md',
  className,
  showBorder = false,
  showShadow = false,
  onClick,
  'data-testid': dataTestId
}) {
  // Get cache functions (will be no-op if not wrapped in provider)
  const { getCachedAvatar, cacheAvatar } = useAvatarCache();
  
  // Try to get cached avatar URL if userId is provided
  const cachedUrl = userId ? getCachedAvatar(userId) : null;
  
  // Prefer the provided avatarUrl (from API/state — authoritative) over cache (may be stale).
  // Cache serves as fallback when avatarUrl isn't available yet (initial render before API loads).
  const effectiveUrl = avatarUrl || cachedUrl;
  
  // Check if image is in browser cache synchronously on first render
  const checkBrowserCache = (url) => {
    if (!url) return false;
    // Create a test image and check if it's already complete (cached)
    const img = new Image();
    img.src = url;
    return img.complete && img.naturalWidth > 0;
  };
  
  // Initialize imageLoaded based on browser cache check
  // If we have a cachedUrl from AvatarCacheContext, we've seen this image before — show it immediately
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(() => !!cachedUrl || checkBrowserCache(effectiveUrl));
  
  // Track the current URL to reset states when it changes
  const [currentUrl, setCurrentUrl] = useState(effectiveUrl);
  
  // Reset error/loaded state when URL changes
  if (effectiveUrl !== currentUrl) {
    setCurrentUrl(effectiveUrl);
    setImageError(false);
    // If URL is cached, trust it; otherwise check browser cache
    setImageLoaded(!!cachedUrl || checkBrowserCache(effectiveUrl));
  }
  
  // Cache the avatar URL when it's provided and differs from what's cached
  useEffect(() => {
    if (userId && avatarUrl && avatarUrl !== cachedUrl) {
      cacheAvatar(userId, avatarUrl);
    }
  }, [userId, avatarUrl, cachedUrl, cacheAvatar]);
  
  // Memoize computed values — defensive: ensure name is never null before any .trim()
  const safeName = name || '';
  const initials = useMemo(() => getInitials(safeName), [safeName]);
  const gradient = useMemo(() => getAvatarGradient(safeName || userId), [safeName, userId]);
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  
  // Determine if we should show the image
  const showImage = effectiveUrl && !imageError;
  
  // Build class names
  const containerClasses = cn(
    'relative rounded-full overflow-hidden flex-shrink-0',
    'flex items-center justify-center font-semibold text-white',
    `bg-gradient-to-br ${gradient}`,
    sizeClass,
    showBorder && 'ring-2 ring-white',
    showShadow && 'shadow-md',
    onClick && 'cursor-pointer hover:opacity-90 transition-opacity',
    className
  );
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  return (
    <div 
      className={containerClasses}
      onClick={onClick}
      data-ui="avatar"
      data-testid={dataTestId}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Image layer - shown when URL exists and hasn't errored */}
      {showImage && (
        <img
          src={effectiveUrl}
          alt={name || 'User avatar'}
          className={cn(
            'absolute inset-0 w-full h-full object-cover',
            'transition-opacity duration-200',
            imageLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      )}
      
      {/* Initials fallback - shown when no image or image loading/errored */}
      {(!showImage || !imageLoaded) && (
        <span className="select-none">
          {initials}
        </span>
      )}
    </div>
  );
}

/**
 * Export utilities for use elsewhere
 */
export { AVATAR_GRADIENTS };

export default UserAvatar;
